import React, { useState } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, MenuItem, Select, FormControl, InputLabel, Typography, IconButton, OutlinedInput
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

function HolidayMngGrid({ holidayList = [], isLoading, filterYear, setFilterYear, onSave, onDelete, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [holidayDate, setHolidayDate] = useState('');
  const [workType, setWorkType] = useState('FULL');
  const [comment, setComment] = useState('');

  const displayList = Array.isArray(holidayList) ? holidayList : (holidayList?.data || []);

  const handleYearChange = (value) => {
    const nextYear = Number(value);
    if (!isNaN(nextYear) && setFilterYear) {
      setFilterYear(nextYear);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setTargetId(item.id || item.holidayUuid || null);
      setHolidayDate(item.holidayDate || '');
      setWorkType(item.workType || 'FULL');
      setComment(item.comment || '');
    } else {
      setTargetId(null);
      setHolidayDate(`${filterYear}-01-01`);
      setWorkType('FULL');
      setComment('');
    }
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleFormSubmit = () => {
    if (!holidayDate) return;

    const sessionUserUuid = JSON.parse(localStorage.getItem('user')).usrUuid || '';

    onSave({
      id: targetId,
      holidayUuid: targetId,
      holidayDate,
      workType,
      comment,
      regId: sessionUserUuid,
      chgId: sessionUserUuid
    });
    handleCloseModal();
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <OutlinedInput
          size="small"
          value={filterYear}
          onChange={(e) => handleYearChange(e.target.value)}
          startAdornment={
            <IconButton 
              size="small" 
              onClick={() => handleYearChange(filterYear - 1)}
              sx={{ p: '2px', mr: 1, color: '#64748b' }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          }
          endAdornment={
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
              <Typography variant="body2" sx={{ fontWeight: '600', mx: 1, userSelect: 'none' }}>
                년
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => handleYearChange(filterYear + 1)}
                sx={{ p: '2px', ml: 0.5, color: '#64748b' }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          inputProps={{
            style: { 
              textAlign: 'center', 
              fontWeight: '600', 
              width: '55px', 
              padding: '6px 0',
              color: '#1e293b'
            }
          }}
          sx={{ 
            borderRadius: '8px', 
            backgroundColor: '#ffffff',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.02)',
            height: '36px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4f46e5', borderWidth: '1px' }
          }}
        />

        <Button variant="contained" color="primary" onClick={() => handleOpenModal(null)} sx={{ height: '36px', borderRadius: '8px', fontWeight: '600' }}>
          신규 휴일 등록
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: '0px 4px 20px rgba(0,0,0,0.04)', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: '600', color: '#475569' }}>휴일 날짜</TableCell>
              <TableCell sx={{ fontWeight: '600', color: '#475569' }}>휴일 유형</TableCell>
              <TableCell sx={{ fontWeight: '600', color: '#475569' }}>코멘트 (설명)</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#475569', width: '110px' }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayList.length > 0 ? (
              displayList.map((row, idx) => (
                <TableRow key={idx} hover sx={{ '&:last-child transform td, &:last-child th': { border: 0 } }}>
                  <TableCell sx={{ color: '#334155' }}>{row.holidayDate}</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: row.workType === 'FULL' ? '#EF4444' : '#F59E0B' }}>
                    {row.workType === 'FULL' ? '종일 휴일 (FULL)' : '반일 휴가 (HALF)'}
                  </TableCell>
                  <TableCell sx={{ color: '#475569' }}>{row.comment || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(row)} color="info" sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(row.id || row.holidayUuid)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary', fontSize: '13px' }}>
                  {filterYear}년도에 등록된 휴일 정보가 존재하지 않습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px', p: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: '700', pb: 1, color: '#1e293b' }}>
          {targetId ? '📝 휴일 정보 수정' : '✨ 신규 휴일 등록'}
        </DialogTitle>
        
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <div>
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: '600', mb: 1, pl: 0.5, fontSize: '13px' }}>
              휴일 날짜
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={holidayDate}
              onChange={(e) => setHolidayDate(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </div>

          <FormControl fullWidth>
            <InputLabel id="work-type-label" sx={{ color: '#475569', fontWeight: '500' }}>휴일 유형</InputLabel>
            <Select
              labelId="work-type-label"
              value={workType}
              label="휴일 유형"
              onChange={(e) => setWorkType(e.target.value)}
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="FULL">종일 휴일 (FULL)</MenuItem>
              <MenuItem value="HALF">반일 휴가 (HALF)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="코멘트 (설명 입력)"
            type="text"
            fullWidth
            multiline
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="예: 추석 연휴, 가정의 날 단축 근무 등"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 0.5 }}>
          <Button onClick={handleCloseModal} color="inherit" sx={{ fontWeight: '600', borderRadius: '6px' }}>취소</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={!holidayDate} sx={{ fontWeight: '600', borderRadius: '6px' }}>
            {targetId ? '수정 완료' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HolidayMngGrid;
